import { ref, watch, type Ref } from 'vue'
import { toast } from '@/components/ui/toast'

export interface FormHandlerOptions<T> {
  initialData: T
  validateFn?: (data: T) => string | null
  resetAfterSuccess?: boolean
  loadFrom?: Ref<unknown> // For edit forms that load data from props
  mapFromSource?: (source: unknown) => T // Transform loaded data to form format
}

export interface FormHandlerResult<T> {
  form: Ref<T>
  handleSubmit: (submitFn: (data: T) => Promise<{ success: boolean; error?: string }>) => Promise<void>
  resetForm: () => void
  isSubmitting: Ref<boolean>
}

export function useFormHandler<T>(options: FormHandlerOptions<T>): FormHandlerResult<T> {
  const form = ref<T>(structuredClone(options.initialData)) as Ref<T>
  const isSubmitting = ref(false)

  // Watch for external data changes (for edit forms)
  if (options.loadFrom && options.mapFromSource) {
    watch(
      options.loadFrom,
      (newData) => {
        if (newData) {
          form.value = options.mapFromSource!(newData)
        }
      },
      { immediate: true }
    )
  }

  const resetForm = () => {
    form.value = structuredClone(options.initialData)
  }

  const handleSubmit = async (
    submitFn: (data: T) => Promise<{ success: boolean; error?: string }>
  ) => {
    // Validate if validator provided
    if (options.validateFn) {
      const validationError = options.validateFn(form.value)
      if (validationError) {
        toast({
          title: validationError,
          variant: "destructive"
        })
        return
      }
    }

    isSubmitting.value = true

    try {
      const result = await submitFn(form.value)

      if (result.success) {
        if (options.resetAfterSuccess) {
          resetForm()
        }
      } else {
        toast({
          title: result.error || 'Operation failed',
          variant: "destructive"
        })
      }
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Unexpected error occurred',
        variant: "destructive"
      })
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    form,
    handleSubmit,
    resetForm,
    isSubmitting
  }
}

// Common validators
export const validators = {
  required: (field: string) => (data: any) => {
    if (!data[field]?.toString().trim()) {
      return `${field} is required`
    }
    return null
  },
  
  displayName: (data: { name: string }) => {
    if (!data.name?.trim()) {
      return 'Display name is required'
    }
    return null
  }
}